/**
 * 实验包加载器。
 *
 * 技术设计 §12.2 未列本文件：§4.1 的 `LabEngine.load(pack)` 收的是**已构建好的 pack 对象**，
 * 「怎么把 JSON 变成 pack」没有落点，故补在 data/ 层，与 schemas / validators 同层。
 *
 * 取包方式用 import.meta.glob 读工程根的 `experiments/`：
 * - 保住 §12.2 把 experiments/ 与 src/ 平级的布局，不为迁就 Vite 的 public/ 而挪目录；
 * - P0 无后端也不该有后端（§21.3 平台轨要等 S0）；
 * - S2 接 Pool 时只需在这里加一个「按 URL + versionId 取包」的分支，LabEngine 侧零改动。
 */

import type { ExperimentPack, ExternalRef } from "./schemas.js";
import { validateExperimentPack } from "./validators.js";

const PACK_FILES = import.meta.glob<unknown>("../../experiments/**/*.json", { import: "default" });

const EXPERIMENTS_ROOT = "experiments/";

/** glob 的 key 形如 `../../experiments/oxygen-kmno4/experiment.json`，统一成 `oxygen-kmno4/experiment.json`。 */
function toPackRelativePath(globKey: string): string | null {
  const index = globKey.indexOf(EXPERIMENTS_ROOT);
  return index === -1 ? null : globKey.slice(index + EXPERIMENTS_ROOT.length);
}

function findLoader(packRelativePath: string): (() => Promise<unknown>) | null {
  for (const [globKey, loader] of Object.entries(PACK_FILES)) {
    if (toPackRelativePath(globKey) === packRelativePath) {
      return loader as () => Promise<unknown>;
    }
  }
  return null;
}

/** 列出 experiments/ 下所有含 experiment.json 的实验包目录名。 */
export function listPackDirs(): string[] {
  const dirs: string[] = [];
  for (const globKey of Object.keys(PACK_FILES)) {
    const relative = toPackRelativePath(globKey);
    if (relative?.endsWith("/experiment.json")) {
      dirs.push(relative.slice(0, -"/experiment.json".length));
    }
  }
  return dirs.sort();
}

async function resolveRef(packDir: string, spec: ExternalRef, field: string): Promise<ExternalRef> {
  const loader = findLoader(`${packDir}/${spec.ref}`);
  if (!loader) {
    throw new Error(`实验包 ${packDir} 的 ${field} 指向 "${spec.ref}"，但该文件不存在`);
  }
  return { ...spec, resolved: await loader() };
}

async function resolveRefArray(
  packDir: string,
  specs: ExternalRef[],
  field: string,
): Promise<ExternalRef[]> {
  return Promise.all(specs.map((spec, index) => resolveRef(packDir, spec, `${field}[${index}]`)));
}

/**
 * 展开实验包里的外部引用。P0 只把 JSON 取回来挂在 `resolved` 上，不解析其语义
 * （mission/rules 归 P2、animations 归 P3、assessment 归 P4）。
 */
async function expandRefs(packDir: string, pack: ExperimentPack): Promise<ExperimentPack> {
  const [mission, rules, knowledge, animations] = await Promise.all([
    resolveRef(packDir, pack.mission, "mission"),
    resolveRefArray(packDir, pack.rules, "rules"),
    resolveRefArray(packDir, pack.knowledge, "knowledge"),
    resolveRefArray(packDir, pack.animations, "animations"),
  ]);

  const expanded: ExperimentPack = { ...pack, mission, rules, knowledge, animations };
  if (pack.assessment) {
    expanded.assessment = await resolveRef(packDir, pack.assessment, "assessment");
  }
  return expanded;
}

/**
 * 加载一个实验包：取 experiment.json → 校验 → 展开 ref。
 * 校验失败时抛出，错误消息含全部问题（一次看全，不用修一个跑一次）。
 */
export async function loadExperimentPack(packDir: string): Promise<ExperimentPack> {
  const loader = findLoader(`${packDir}/experiment.json`);
  if (!loader) {
    throw new Error(
      `找不到实验包 "${packDir}"。experiments/ 下可用的包：${listPackDirs().join(", ") || "（空）"}`,
    );
  }

  const result = validateExperimentPack(await loader());
  if (!result.ok) {
    throw new Error(`实验包 ${packDir} 校验失败（${result.errors.length} 项）：\n- ${result.errors.join("\n- ")}`);
  }

  return expandRefs(packDir, result.value);
}
